import { useState, useEffect } from "react";
import { useContent } from "@/context/ContentContext";
import { Save, DollarSign } from "lucide-react";

// Defined outside parent component to prevent re-mounting on every render
function Field({ label, name, value, onChange, type = "text", placeholder, textarea, hint }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">{label}</label>
      {hint && <p className="text-xs text-[#94A3B8] mb-1">{hint}</p>}
      {textarea ? (
        <textarea rows={2} name={name} value={value || ""} onChange={onChange}
          className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4] resize-none"
          data-testid={`pricing-${name}`} placeholder={placeholder} />
      ) : (
        <input type={type} name={name} value={value ?? ""} onChange={onChange}
          className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]"
          data-testid={`pricing-${name}`} placeholder={placeholder} />
      )}
    </div>
  );
}

export default function PricingManager() {
  const { content, updateSection } = useContent();
  const [pricing, setPricing] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (content.pricing) setPricing({ ...content.pricing });
  }, [content.pricing]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setPricing(p => ({ ...p, [name]: type === "number" ? Number(value) : value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSection("pricing", pricing);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="pricing-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Montserrat'] font-bold text-2xl text-[#0F172A] mb-1">Precios y Tarifas</h1>
          <p className="text-[#475569] font-['Montserrat'] text-sm">Actualiza los precios del campus</p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-pricing-btn"
          className={`flex items-center gap-2 px-5 py-2 rounded-lg font-['Montserrat'] font-semibold text-sm transition-all ${saved ? "bg-green-500 text-white" : "bg-[#0072A4] text-white hover:bg-[#005A80]"} disabled:opacity-60`}>
          <Save size={15} /> {saving ? "Guardando..." : saved ? "¡Guardado!" : "Guardar"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section header */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 lg:col-span-2">
          <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4">Encabezado de la sección</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Etiqueta (pill)</label>
              <input name="section_label" value={pricing.section_label || ""} onChange={handleChange} data-testid="pricing-section-label"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="Precios y Tarifas" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Título principal</label>
              <input name="title" value={pricing.title || ""} onChange={handleChange} data-testid="pricing-title-input"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="Invierte en su aventura" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-[#0F172A] font-['Montserrat'] mb-1">Subtítulo</label>
              <input name="subtitle" value={pricing.subtitle || ""} onChange={handleChange} data-testid="pricing-subtitle-input"
                className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm font-['Montserrat'] focus:outline-none focus:border-[#0072A4]" placeholder="Precios por semana. ¡Descuento especial para hermanos/as!" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
          <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <DollarSign size={18} color="#0072A4" /> Precios principales
          </h3>
          <div className="space-y-4">
            <Field label="Precio por semana (€)" name="precio_semana" value={pricing.precio_semana} onChange={handleChange} type="number" placeholder="140" />
            <Field label="Precio hermanos/as (€)" name="precio_hermanos" value={pricing.precio_hermanos} onChange={handleChange} type="number" placeholder="130" hint="Precio por semana con descuento familiar" />
            <Field label="Símbolo moneda" name="moneda" value={pricing.moneda} onChange={handleChange} placeholder="€" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
          <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4">Servicios opcionales</h3>
          <div className="space-y-4">
            <Field label="Precio madrugadores (€/semana)" name="precio_madrugadores" value={pricing.precio_madrugadores} onChange={handleChange} type="number" placeholder="20" />
            <Field label="Precio recogida ampliada (€/semana)" name="precio_recogida" value={pricing.precio_recogida} onChange={handleChange} type="number" placeholder="20" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 lg:col-span-2">
          <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4">Textos informativos</h3>
          <div className="space-y-4">
            <Field label="Nota sobre precios" name="nota" value={pricing.nota} onChange={handleChange} textarea placeholder="Texto informativo sobre los precios..." />
            <Field label="Método de pago" name="metodo_pago" value={pricing.metodo_pago} onChange={handleChange} textarea placeholder="Transferencia bancaria. Confirmar disponibilidad..." />
            <Field label="Información de reserva" name="reserva_info" value={pricing.reserva_info} onChange={handleChange} textarea placeholder="La plaza queda confirmada..." />
          </div>
        </div>

        {/* Preview */}
        <div className="bg-[#F0F8FF] rounded-xl border border-[#0072A4]/20 p-6 lg:col-span-2">
          <h3 className="font-['Montserrat'] font-semibold text-[#0F172A] mb-4">Vista previa de precios</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Estándar", value: pricing.precio_semana, suffix: "€/sem", color: "#0072A4" },
              { label: "Hermanos/as", value: pricing.precio_hermanos, suffix: "€/sem", color: "#F58220" },
              { label: "Madrugadores", value: `+${pricing.precio_madrugadores}`, suffix: "€/sem", color: "#3CB44B" },
              { label: "Recogida ampliada", value: `+${pricing.precio_recogida}`, suffix: "€/sem", color: "#F59E0B" },
            ].map((p) => (
              <div key={p.label} className="bg-white rounded-xl p-4 text-center border border-[#E2E8F0]" data-testid={`price-preview-${p.label}`}>
                <div className="font-['Montserrat'] font-bold text-3xl" style={{ color: p.color }}>{p.value}{pricing.moneda || "€"}</div>
                <div className="font-['Montserrat'] text-xs text-[#475569] mt-1">{p.label}</div>
                <div className="font-['Montserrat'] text-xs text-[#94A3B8]">{p.suffix}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
